import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useHttp } from "hooks/http.hook";
import { API_VERSION_V2 } from "endpointsConst";

import styles from "./styles.module.scss";

interface ReviewsSummary {
    notPaidCount: number | undefined;
    paidWithDelayCount: number | undefined;
    paidInTimeCount: number | undefined;
}

interface PaymentStatusSummaryProps {
    companyId: number | string | undefined;
}

export default function PaymentStatusSummary({ companyId }: PaymentStatusSummaryProps) {
    const [summary, setSummary] = useState<ReviewsSummary | null>(null);
    const { request } = useHttp();
    const { t } = useTranslation();

    useEffect(() => {
        if (!companyId) return;
        request(`${API_VERSION_V2}/companies/${companyId}/reviews-summary`)
            .then((res: { data: ReviewsSummary }) => {
                setSummary(res.data);
            })
            .catch(() => {});
    }, [companyId, request]);

    if (!summary) return null;

    const hasData =
        !!summary.paidInTimeCount ||
        !!summary.paidWithDelayCount ||
        !!summary.notPaidCount;

    if (!hasData) return null;

    return (
        <div className={styles.container}>
            <span className={styles.label}>{t("dialog.review.paymentStatus")}</span>
            <div className={styles.badges}>
                <div className={styles.badge}>
                    <span className={`${styles.dot} ${styles.dot_green}`} />
                    <span className={styles.badgeLabel}>{t("dialog.review.paymentOnTime")}</span>
                    <span className={`${styles.count} ${styles.count_green}`}>
                        {summary.paidInTimeCount ?? 0}
                    </span>
                </div>
                <div className={styles.badge}>
                    <span className={`${styles.dot} ${styles.dot_yellow}`} />
                    <span className={styles.badgeLabel}>{t("dialog.review.paymentDelayed")}</span>
                    <span className={`${styles.count} ${styles.count_yellow}`}>
                        {summary.paidWithDelayCount ?? 0}
                    </span>
                </div>
                <div className={styles.badge}>
                    <span className={`${styles.dot} ${styles.dot_red}`} />
                    <span className={styles.badgeLabel}>{t("dialog.review.paymentNotPaid")}</span>
                    <span className={`${styles.count} ${styles.count_red}`}>
                        {summary.notPaidCount ?? 0}
                    </span>
                </div>
            </div>
        </div>
    );
}
